import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionItem, Anchor, Box, Drop, MoreIcon, useResponsive } from '@common-fe/common-fe';
import styled from 'styled-components';

import { Roles } from '@/common/constants';
import ROUTES from '@/common/routes';
import AppButton from '@/components/controls/AppButton';
import ExcessContributionWithdrawModal from '@/components/modals/ExcessContributionWithdrawModal/ExcessContributionWithdrawModal';
import { useExcessContributionAvailable } from '@/components/modals/ExcessContributionWithdrawModal/useExcessContributionAvailable';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import { useTermsAndConditionsDocsQuery } from '@/modules/investments/docs';

import { FundManagementOption } from '../../../FundsManagementBanner/hooks/useFundManagementOptions';

import { FundsManagementButton } from './FundsManagementButton';

const StyledAnchor = styled(Anchor)`
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacings.spacing4} ${theme.spacings.spacing8}`};
  border-radius: ${({ theme }) => theme.spacings.spacing4};
  &:focus,
  &:hover {
    text-decoration: none;
    background-color: ${({ theme }) => theme.colors.buttonSecondaryContainer};
  }
`;

interface Props {
  accountId: string;
  externalButtons?: FundManagementOption[];
  isCurrentHSA?: boolean;
  isOpenHSAExist?: boolean;
}

const DisclosureButton: React.FC<Props> = ({ externalButtons, accountId, isCurrentHSA, isOpenHSAExist }) => {
  const { t } = useTranslation();
  const { isExcessContributionAvailable } = useExcessContributionAvailable();
  const [isModalVisible, setModalVisible] = useState(false);
  const { HSAAgreementLink, termsAndConditionsLink } = useTermsAndConditionsDocsQuery();
  const history = useHistory();
  const currentRef = useRef(null);
  const [visiblePopup, setVisiblePopup] = useState(false);
  const isLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const handleHide = useCallback(() => {
    setVisiblePopup(false);
  }, []);

  const handleOpenStatementsPage = useCallback(
    () => history.push(ROUTES.STATEMENTS(accountId)),
    [history, accountId]
  );

  const handleOpenTaxForms = useCallback(
    () => history.push(ROUTES.TAX_FORMS(accountId)),
    [history, accountId]
  );
  const { isMobile: isMobileMode } = useResponsive();
  return (
    <Box ref={currentRef}>
      {isModalVisible ? (
        <ExcessContributionWithdrawModal onSetVisible={setModalVisible} isEditMode accountId={accountId} />
      ) : null}
      {isMobileMode ? (
        <AppButton
          testId="DisclosureButton"
          buttonType="secondary"
          width="40px"
          color="grey"
          style={{ padding: 0, minHeight: '40px', maxHeight: '40px' }}
          onClick={() => {
            if (!visiblePopup) {
              setVisiblePopup(!visiblePopup);
            }
          }}
        >
          <MoreIcon color="iconPrimary" />
        </AppButton>
      ) : (
        <AppButton
          testId="DisclosureButton"
          buttonType="secondary"
          width="130px"
          color="grey"
          rightIcon={<MoreIcon color="iconPrimary" />}
          onClick={() => {
            if (!visiblePopup) {
              setVisiblePopup(!visiblePopup);
            }
          }}
        >
          {t('More')}
        </AppButton>
      )}

      {visiblePopup && (
        <Drop
          target={currentRef?.current || undefined}
          onEsc={handleHide}
          onClickOutside={handleHide}
          round="fieldRound"
          background="canvas"
          align={{ top: 'bottom', right: 'right' }}
          data-testid="Drop-content-wrapper"
        >
          <Box pad="s" round="fieldRound" border={{ color: 'border1' }} width={{ min: 'medium' }}>
            {!!externalButtons?.length && (
              <Box
                pad={{ bottom: 'spacing8' }}
                margin={{ bottom: 'spacing8' }}
                border={{ color: 'border1', side: 'bottom' }}
              >
                {externalButtons.map((button) => (
                  <FundsManagementButton
                    fundManagementButton={button}
                    key={button.title}
                    additionalAction={() => setVisiblePopup(false)}
                  />
                ))}
              </Box>
            )}
            {isCurrentHSA && (
              <>
                <Box pad={{ vertical: 'spacing4', horizontal: 'spacing8' }}>
                  <AccordionItem text="Legal Documents & Disclosures">
                    <Box margin={{ left: 'spacing32', top: 'spacing12' }}>
                      <StyledAnchor
                        href={HSAAgreementLink}
                        size="medium"
                        color="textBody"
                        target="_blank"
                        weight={400}
                      >
                        {t('HSA Agreement')}
                      </StyledAnchor>
                    </Box>
                  </AccordionItem>
                </Box>

                <Box margin={{ top: 'xxxs' }}>
                  <StyledAnchor
                    href={termsAndConditionsLink}
                    size="medium"
                    color="textBody"
                    target="_blank"
                    weight={400}
                  >
                    {t('Investment Terms & Conditions')}
                  </StyledAnchor>
                </Box>
              </>
            )}
            {!isLimitedAccess && (
              <>
                <Box margin={{ top: 'xxxs' }}>
                  <StyledAnchor
                    onClick={handleOpenStatementsPage}
                    size="medium"
                    color="textBody"
                    target="_blank"
                    weight={400}
                  >
                    {t('Statements')}
                  </StyledAnchor>
                </Box>
                {isCurrentHSA && (
                  <Box margin={{ top: 'xxxs' }}>
                    <StyledAnchor
                      onClick={handleOpenTaxForms}
                      size="medium"
                      color="textBody"
                      target="_blank"
                      datat-testid="tax-form_link"
                      weight={400}
                    >
                      {t('Tax Forms')}
                    </StyledAnchor>
                  </Box>
                )}
                {isExcessContributionAvailable && isOpenHSAExist && (
                  <Box margin={{ top: 'xxxs' }}>
                    <StyledAnchor
                      data-testId="request-excess-contribution-withdraw-link"
                      onClick={() => setModalVisible(true)}
                      size="medium"
                      color="textBody"
                      weight={400}
                    >
                      {t('Request Excess Contribution Withdraw')}
                    </StyledAnchor>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Drop>
      )}
    </Box>
  );
};
export default DisclosureButton;
